"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import {
  ArrowLeft,
  ChevronRight,
  Loader2,
  Sparkles,
  Zap,
  BarChart3,
  BarChart2,
  BarChart,
  Layers,
  Globe,
  LayoutTemplate,
  LayoutGrid,
  Users,
  CalendarDays,
  KanbanSquare,
  Bot,
  MessageCircle,
  MessageSquare,
  Briefcase,
  Code2,
  Code,
  Smartphone,
  Palette,
  MonitorSmartphone,
  Moon,
  Gauge,
  Search as SearchIcon,
  Play,
  ImageIcon,
  TrendingUp,
  Table2,
  DollarSign,
  BookOpen,
  FileText,
  FileEdit,
  Mail,
  Star,
  Award,
  Grid,
  ShoppingCart,
  Activity,
  Clock,
  Flag,
  Hash,
  Trophy,
  Building2,
  Settings,
  Brain,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { customToast } from "@/lib/toast";
import {
  cloneErrorMessage,
  isRetryableCloneError,
} from "@/lib/clone/error-copy";
import { createClient } from "@/lib/supabase/client";
import { SignupModal } from "@/components/auth/signup-modal";
import { PandaBrandLogo } from "@/components/panda-brand-logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { BuiltWithBadge } from "@/components/built-with-badge";

interface HighlightEntry {
  icon: string;
  title: string;
  description: string;
}

export interface DBTemplate {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string | null;
  icon_name: string | null;
  screenshot_url: string | null;
  starter_prompt: string | null;
  source_project_id: string | null;
  use_count: number;
  sort_order: number;
  highlights: HighlightEntry[] | null;
  long_description: string | null;
}

/** Maps icon name strings (from DB) to lucide-react components. */
const ICON_BY_NAME: Record<string, typeof Code2> = {
  Sparkles, Zap, BarChart3, BarChart2, BarChart, Layers, Globe, LayoutTemplate,
  LayoutGrid, Users, CalendarDays, KanbanSquare, Bot, MessageCircle,
  MessageSquare, Briefcase, Code2, Code, Smartphone, Palette, MonitorSmartphone,
  Moon, Gauge, Search: SearchIcon, TrendingUp, Table2, DollarSign, BookOpen,
  FileText, FileEdit, Mail, Star, Award, Grid, ShoppingCart, Activity, Clock,
  Flag, Hash, Trophy, Building2, Settings, Brain, ImageIcon, Play,
};

const FALLBACK_IMAGES: Record<string, string> = {
  "base-nextjs": "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=720&h=480&q=82",
  "tpl-landing": "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=720&h=480&q=82",
  "tpl-dashboard": "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=720&h=480&q=82",
  "tpl-portfolio": "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=720&h=480&q=82",
  "tpl-blog": "https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=720&h=480&q=82",
  "tpl-store": "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=720&h=480&q=82",
  "tpl-crm": "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=720&h=480&q=82",
  "tpl-booking": "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=720&h=480&q=82",
  "tpl-project": "https://images.unsplash.com/photo-1611224923853-80b023f02d71?auto=format&fit=crop&w=720&h=480&q=82",
  "tpl-ai": "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=720&h=480&q=82",
  "tpl-social": "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=720&h=480&q=82",
  "tpl-marketplace": "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=720&h=480&q=82",
};

/** Key highlights per template category. */
/** Fallback highlights when DB has none for a template. */
const DEFAULT_HIGHLIGHTS: HighlightEntry[] = [
  { icon: "Smartphone", title: "Mobile-First Responsive", description: "Looks great on every device — phone, tablet, and desktop." },
  { icon: "Gauge", title: "Performance Optimized", description: "Built with Next.js for fast loading and smooth interactions." },
  { icon: "Palette", title: "Modern Design System", description: "Clean, consistent UI with Tailwind CSS and shadcn/ui components." },
  { icon: "Moon", title: "Dark Mode Support", description: "Built-in light and dark themes that follow system preferences." },
];

export default function TemplateDetailClient({
  slug,
  initialTemplate,
}: {
  slug: string;
  initialTemplate: DBTemplate | null;
}) {
  const router = useRouter();
  // Seed from the server-fetched template so the first paint carries real
  // content (good for SEO and avoids a spinner flash). Only fall back to a
  // client fetch when the server couldn't provide it (e.g. dev mode without
  // a service-role key).
  const [template, setTemplate] = useState<DBTemplate | null>(initialTemplate);
  const [loading, setLoading] = useState(initialTemplate === null);
  const [isUsing, setIsUsing] = useState(false);
  const [user, setUser] = useState<{ email?: string } | null>(null);
  const [signupOpen, setSignupOpen] = useState(false);

  // Template detail shows the screenshot only. The former in-browser live
  // preview ran in a WebContainer (desktop-only, cross-origin-isolated); that
  // backend was removed, and these template source projects have no deployed
  // URL to iframe instead — so the screenshot is the preview. The live app is
  // seen once the user clicks "Use this template" and a real project boots.

  // Reset modal state on bfcache restore so the "Sign up" button still
  // works after the user starts an OAuth redirect from inside the modal
  // and then hits Back. See app/page.tsx for the full explanation.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setSignupOpen(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Fetch auth always; fetch the template only if the server didn't supply it.
  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) setUser({ email: data.user.email });
    });

    if (initialTemplate) return;

    fetch(`/api/templates/${slug}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.template) setTemplate(data.template);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug, initialTemplate]);

  async function handleUseTemplate() {
    if (!user) {
      setSignupOpen(true);
      return;
    }
    if (!template) return;

    if (isUsing) return;
    setIsUsing(true);
    try {
      const res = await fetch(`/api/templates/${template.slug}/use`, {
        method: "POST",
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data?.error === "project_limit_reached") {
        router.push(`/project/${uuidv4()}?show_limit=true`);
        return;
      }
      if (res.status === 403 && data?.error === "email_verification_required") {
        // The global gate modal already explains this; don't stack a vaguer
        // error toast on top of it.
        setIsUsing(false);
        return;
      }
      if (res.status === 402) {
        customToast.error("You're out of credits. Top up to keep building.", {
          action: { label: "Upgrade", onClick: () => router.push("/pricing") },
        });
        setIsUsing(false);
        return;
      }
      if (res.status === 422) {
        // The route sends the reason with the code — a template whose source has
        // vanished and one the health gate refused are both 422 but need
        // different copy, and only the server knows which one this is.
        customToast.error(
          data?.message ||
            "This template's files are no longer available, so it can't be cloned.",
        );
        setIsUsing(false);
        return;
      }
      // 503 = the lookup itself failed (ours, retryable); 404 = the template is
      // gone or unpublished (permanent). Both used to fall into the catch below
      // and read as the same "try again" — right for one, wrong for the other.
      if (!res.ok) {
        const code: string | undefined = data?.error;
        customToast.error(
          cloneErrorMessage(
            code,
            data?.message,
            "Couldn't start from this template. Try again.",
          ),
          isRetryableCloneError(code, res.status)
            ? { action: { label: "Retry", onClick: () => void handleUseTemplate() } }
            : undefined,
        );
        setIsUsing(false);
        return;
      }
      if (!data?.id) throw new Error("Failed to use template");
      // ?from=template → the workspace shows "Loading your template" instead of
      // "Restoring your session" for this first open (see lib/preview/load-kind).
      router.push(`/project/${data.id}?from=template`);
    } catch (err) {
      console.error("Use template error:", err);
      customToast.error("Couldn't start from this template. Try again.");
      setIsUsing(false);
    }
  }

  const imgSrc = template
    ? template.screenshot_url ??
      FALLBACK_IMAGES[template.slug] ??
      "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=720&h=480&q=82"
    : "";

  const highlights: HighlightEntry[] =
    template?.highlights && template.highlights.length > 0
      ? template.highlights
      : DEFAULT_HIGHLIGHTS;


  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600 dark:text-emerald-400" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-foreground">
        <p className="text-lg font-medium">Template not found</p>
        <Link
          href="/templates"
          className="text-sm font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
        >
          &larr; Back to templates
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-8">
          <Link
            href="/"
            className="shrink-0 text-foreground transition-opacity hover:opacity-90"
            aria-label="Panda home"
          >
            <PandaBrandLogo />
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {user ? (
              <Link
                href="/projects"
                className="rounded-full bg-panda-gradient-success px-4 py-1.5 text-sm font-semibold text-white transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Projects
              </Link>
            ) : (
              <button
                onClick={() => setSignupOpen(true)}
                className="rounded-full bg-panda-gradient-success px-4 py-1.5 text-sm font-semibold text-white transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Get started
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-12">
        {/* Breadcrumb */}
        <div className="mb-8 flex items-center gap-1.5 text-sm text-muted-foreground">
          <Link
            href="/templates"
            className="transition-colors hover:text-foreground"
          >
            Templates
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          {template.category && (
            <>
              <span className="text-muted-foreground">
                {template.category}
              </span>
              <ChevronRight className="h-3.5 w-3.5" />
            </>
          )}
          <span className="font-medium text-foreground">{template.name}</span>
        </div>

        {/* Two-column layout */}
        <div className="grid gap-10 lg:grid-cols-[1fr_420px] lg:gap-14">
          {/* Left — Screenshot / Preview */}
          {/* min-w-0: grid items default to min-width:auto, which lets the
              preview's aspect-ratio box grow to its intrinsic width and
              overflow the track on mobile. min-w-0 makes it respect the
              column width instead. */}
          <div className="min-w-0 space-y-4">
            <div className="overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
              {/* Browser-like top bar */}
              <div className="flex items-center justify-between gap-2 border-b border-border bg-card px-4 py-2.5">
                <div className="flex min-w-0 items-center gap-2">
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span className="h-3 w-3 rounded-full bg-red-400/80" />
                    <span className="h-3 w-3 rounded-full bg-yellow-400/80" />
                    <span className="h-3 w-3 rounded-full bg-green-400/80" />
                  </div>
                  <div className="ml-3 min-w-0 truncate rounded-md bg-muted px-3 py-1 text-xs text-muted-foreground">
                    {template.name.toLowerCase().replace(/\s+/g, "-")}.panda.dev
                  </div>
                </div>
              </div>

              {/* Preview area — template screenshot */}
              <div className="relative aspect-[16/10] w-full bg-muted">
                <Image
                  src={imgSrc}
                  alt={template.name}
                  fill
                  className="object-cover object-top"
                  sizes="(max-width: 1024px) 100vw, 60vw"
                  quality={90}
                  priority
                  // Template screenshots are user-uploaded Supabase storage
                  // URLs, which the Next image optimizer rejects ("url not
                  // allowed") and renders as a broken image. Load directly,
                  // same as the community cards (which already do this).
                  unoptimized
                />

                <BuiltWithBadge projectId={template.slug} />
              </div>
            </div>

            {/* About section — below the preview */}
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="text-lg font-semibold text-foreground">
                About this template
              </h3>
              {(template.long_description || template.description)
                .split("\n")
                .filter((p) => p.trim())
                .map((paragraph, i) => (
                  <p
                    key={i}
                    className="mt-3 leading-relaxed text-muted-foreground"
                  >
                    {paragraph}
                  </p>
                ))}
            </div>
          </div>

          {/* Right — Info panel */}
          <div className="min-w-0 space-y-6 lg:sticky lg:top-[calc(3.5rem+2rem)] lg:self-start">
            {/* Template header */}
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                {template.name}
              </h1>
              <p className="mt-2 leading-relaxed text-muted-foreground">
                {template.description}
              </p>
              {template.use_count > 0 && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Used by{" "}
                  <span className="font-medium text-foreground">
                    {template.use_count.toLocaleString()}
                  </span>{" "}
                  {template.use_count === 1 ? "creator" : "creators"}
                </p>
              )}
            </div>

            {/* Use template button — this page's clone CTA, and the whole
                reason the page exists. It was flat `bg-orange-500`, which is
                neither the brand gradient nor anything else in the product; it
                just read as "danger" next to a Key Highlights list. Success
                gradient per components/ui/modal-accents.ts, with the
                brightness lift instead of a hover fill because a gradient has
                no `hover:bg-*` counterpart. The label is 16px semibold —
                normal text for WCAG — and the 700/800 stops hold 5.4:1, still
                4.6:1 under the lift. */}
            <button
              onClick={handleUseTemplate}
              disabled={isUsing}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-panda-gradient-success px-6 py-3 text-base font-semibold text-white shadow-sm transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isUsing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating project...
                </>
              ) : (
                "Use this template"
              )}
            </button>

            {/* Key Highlights */}
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground">
                Key Highlights
              </h2>
              <div className="mt-4 space-y-4">
                {highlights.map((h, i) => {
                  const HIcon = ICON_BY_NAME[h.icon] ?? Code2;
                  return (
                    <div key={i} className="flex gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
                        <HIcon className="h-4 w-4" strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">
                          {h.title}
                        </p>
                        <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                          {h.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Back link */}
            <Link
              href="/templates"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Browse all templates
            </Link>
          </div>
        </div>
      </main>

      <SignupModal
        open={signupOpen}
        onOpenChange={setSignupOpen}
        next={`/templates/${slug}`}
      />
    </div>
  );
}
